import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import SinglePost from "./SinglePost";
import Navbar from "./Navbar";
import Home from "./Home";
import About from "./About";
import Contact from "./Contact";
import Conservation from "./Conservation";
import CheckoutStep1 from "./CheckoutStep1";
import CheckoutStep2Wrapper from "./CheckoutStep2";
import EmailFailure from "./EmailFailure";
import KanziSlideshow from "./KanziSlideshow";
import KanziCelebration from "./KanziCelebration";
import KanziCelebrationTitle from "./KanziFadeTitle";
import Mission from "./Mission";
import Store from "./Store";
import SueBiblio from "./SueBiblio";
import Timeline from "./Timeline";
import Login from "./Login";
import Posts from "./Posts";
import CreatePost from "./CreatePost";
import EditPost from "./EditPost";
import Footer from "./Footer";
import DonationPage from "./DonationPage";
import DonationSuccess from "./DonationSuccess";
import DonationEmailFailure from "./DonationEmailFailure";
import Cart from "./Cart";
import "./styles.css";
import Products from "./Products";
import ProductDetails from "./ProductDetails";
import Success from "./Success";
import Failure from "./Failure";
import VideoSection from "./VideoSection";
import PrivacyPolicy from "./PrivacyPolicy";
import SEOManager from "./SEOManager";
import SeoLandingPage from "./SeoLandingPage";
import KeyboardPage from "./KeyboardPage";
import MusicPage from "./MusicPage";
import Wiki from "./Wiki";
import WikiErnstVonGlasersfeld from "./WikiErnstVonGlasersfeld";
import WikiTopicPage from "./WikiTopicPage";
import SueSavageRumbaugh from "./SueSavageRumbaugh";
import ReactGA from "react-ga4";
import "./CookieConsent.css";

const ANALYTICS_CONSENT_KEY = "bhi-analytics-consent";

const getStoredAnalyticsConsent = () => {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
};

const initializeAnalytics = () => {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!measurementId || window.__bhiAnalyticsInitialized) return false;

  ReactGA.initialize(measurementId);
  window.__bhiAnalyticsInitialized = true;
  return true;
};

const scrollToRouteTarget = (location) => {
  window.setTimeout(() => {
    if (location.hash) {
      const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (target) {
        const navbarHeight =
          document.querySelector(".navbar-container")?.getBoundingClientRect().height || 0;
        const offset = navbarHeight + 28;
        const targetTop =
          target.getBoundingClientRect().top + window.scrollY - offset;

        window.scrollTo({
          top: Math.max(targetTop, 0),
          behavior: "auto",
        });
        return;
      }
    }

    window.scrollTo(0, 0);
  }, 0);
};

const LegacyWikiRedirect = () => {
  const location = useLocation();
  const researchPath = location.pathname.replace(/^\/wiki/, "/research");

  return (
    <Navigate
      to={`${researchPath}${location.search}${location.hash}`}
      replace
    />
  );
};

const App = () => {
  const [loggedIn, setLoggedIn] = useState(
    !!localStorage.getItem("accessToken")
  );
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem("cart");
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (e) {
      console.error("Failed to parse cart from localStorage:", e);
      return [];
    }
  });
  const [analyticsConsent, setAnalyticsConsent] = useState(
    () => getStoredAnalyticsConsent() || "unset"
  );

  // Track page views with React Router and scroll to top
  const location = useLocation();
  useEffect(() => {
    if (analyticsConsent !== "accepted") {
      scrollToRouteTarget(location);
      return;
    }

    initializeAnalytics();
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname + location.search,
    });
    scrollToRouteTarget(location);
  }, [location, analyticsConsent]);

  useEffect(() => {
    setLoggedIn(!!localStorage.getItem("accessToken"));
  }, []);

  useEffect(() => {
    console.log("Cart updated in App.js:", cart);
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("cart");
    setCart([]);
    setLoggedIn(false);
  };

  // Function to clear cart explicitly
  const clearCart = () => {
    console.log("Clearing cart in App.js...");
    setCart([]);
    localStorage.removeItem("cart");
    console.log("Cart cleared in App.js");
  };

  const updateAnalyticsConsent = (value) => {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, value);
    setAnalyticsConsent(value);
  };

  return (
    <div className="app-container">
      <SEOManager />
      <Navbar loggedIn={loggedIn} onLogout={handleLogout} cart={cart} />
      <div className="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/kanzi" element={<KanziCelebration />} />
          <Route path="/bonobos" element={<SeoLandingPage page="bonobos" />} />
          <Route
            path="/bonobo-language-research"
            element={<SeoLandingPage page="bonobo-language-research" />}
          />
          <Route
            path="/lexigrams"
            element={<SeoLandingPage page="lexigrams" />}
          />
          <Route path="/posts" element={<Posts />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/Conservation" element={<Conservation />} />
          <Route path="/KanziCelebration" element={<Navigate to="/kanzi" replace />} />
          <Route path="/KanziSlideshow" element={<KanziSlideshow />} />
          <Route
            path="/KanziCelebrationTitle"
            element={<KanziCelebrationTitle />}
          />
          <Route path="/Mission" element={<Mission />} />
          <Route path="/post/:id" element={<SinglePost />} />
          <Route path="/store" element={<Store />} />
          <Route path="/products" element={<Products />} />
          <Route
            path="/product/:id"
            element={<ProductDetails cart={cart} setCart={setCart} />}
          />
          <Route
            path="/cart"
            element={<Cart cart={cart} setCart={setCart} />}
          />
          <Route
            path="/checkout/step1"
            element={<CheckoutStep1 setCart={setCart} />}
          />
          <Route
            path="/checkout/step2"
            element={<CheckoutStep2Wrapper setCart={clearCart} />}
          />
          <Route path="/success" element={<Success />} />
          <Route path="/donation-success" element={<DonationSuccess />} />
          <Route path="/failure" element={<Failure />} />
          <Route path="/email-failure" element={<EmailFailure />} />
          <Route
            path="/donation-email-failure"
            element={<DonationEmailFailure />}
          />
          <Route path="/keyboard" element={<KeyboardPage />} />
          <Route path="/music" element={<MusicPage />} />
          <Route path="/research" element={<Wiki />} />
          <Route path="/research/ernst-von-glasersfeld" element={<WikiErnstVonGlasersfeld />} />
          <Route path="/research/:slug" element={<WikiTopicPage />} />
          <Route path="/wiki/*" element={<LegacyWikiRedirect />} />
          <Route path="/sue-savage-rumbaugh" element={<SueSavageRumbaugh />} />
          <Route path="/video" element={<VideoSection />} />
          <Route path="/SueBiblio" element={<SueBiblio />} />
          <Route path="/history/*" element={<Timeline />} />
          <Route path="/donate" element={<DonationPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/BonoboHopeKeyboard" element={<Navigate to="/keyboard" replace />} />
          <Route
            path="/login"
            element={<Login onLogin={() => setLoggedIn(true)} />}
          />
          {loggedIn && (
            <>
              <Route path="/django-posts" element={<Posts />} />
              <Route path="/create-post" element={<CreatePost />} />
              <Route path="/edit-post/:id" element={<EditPost />} />
              <Route
                path="/logout"
                element={
                  <div>
                    <h2>Are you sure you want to log out?</h2>
                    <button onClick={handleLogout}>Logout</button>
                  </div>
                }
              />
            </>
          )}
        </Routes>
      </div>
      <Footer loggedIn={loggedIn} />
      {analyticsConsent === "unset" && (
        <div className="cookie-consent" role="region" aria-label="Cookie notice">
          <div>
            <strong>Privacy preferences</strong>
            <p>
              We use essential cookies for site functionality and secure
              payments. With your permission, optional analytics cookies help
              us understand site traffic and improve the site.
            </p>
          </div>
          <div className="cookie-consent__actions">
            <Link className="cookie-consent__link" to="/privacy-policy">
              Privacy Policy
            </Link>
            <button
              type="button"
              className="cookie-consent__button cookie-consent__button--secondary"
              onClick={() => updateAnalyticsConsent("rejected")}
            >
              Reject optional cookies
            </button>
            <button
              type="button"
              className="cookie-consent__button cookie-consent__button--primary"
              onClick={() => updateAnalyticsConsent("accepted")}
            >
              Accept cookies
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrap App with Router to provide useLocation context
const AppWithRouter = () => (
  <Router>
    <App />
  </Router>
);

export default AppWithRouter;
